/* mealPlanner.js
 * MealPlanner — global state, step navigation, DOM orchestration, Shopify cart.
 *
 * Flow:
 *   Step 1 (Breakfast)  — duration selector is INTERACTIVE
 *   Step 2 (Lunch)      — duration shown as STATIC badge (locked to step-1 choice)
 *   Step 3 (Snacks)     — duration shown as STATIC badge
 *   …additional steps   — duration shown as STATIC badge
 *   Summary             — review all; "Edit Plan" jumps back to Step 1
 *
 * Events:
 *   Fires 'onDurationChange' CustomEvent on document when duration changes.
 *   MealCard instances and any external listeners react via that event.
 *
 * Depends on: MealItem (mealItem.js), MealCard (mealCard.js),
 *             window.MEAL_CONFIG (meal-plan-config.js)
 */

class MealPlanner {
  constructor(config) {
    this.cfg = config;

    // ── State ─────────────────────────────────────────────────────────────────
    this.state = {
      duration:     parseInt(Object.keys(config.durationMap)[0], 10) || 1,
      currentStep:  0,
      stepStates:   [],
      visitedSteps: new Set([0]),   // tracks which steps the user has reached
      selectedItem: [],             // ordered list of all currently-selected products
      isEditing:    false,          // true when navigating back from summary via Edit
    };

    // ── Caches ────────────────────────────────────────────────────────────────
    this.productCache = {};   // handle → Shopify /products/{handle}.js JSON
    this.mealItems    = {};   // handle → MealItem instance (shared across all steps)
    this.mealCards    = {};   // `${stepIdx}-${modeKey}-${handle}` → MealCard instance

    // ── DOM refs ──────────────────────────────────────────────────────────────
    this._container    = null;
    this._durationSlot = null;
    this._indicator    = null;
    this._stepLabel    = null;
    this._footer       = null;

    // Bound so we can remove in destroy()
    this._onDurationChangeBound = this._handleDurationChange.bind(this);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Public API
  // ═══════════════════════════════════════════════════════════════════════════

  async init() {
    this._container    = document.getElementById('mpb-container');
    this._durationSlot = document.getElementById('mpb-duration-slot');
    this._indicator    = document.getElementById('mpb-step-indicator');
    this._stepLabel    = document.getElementById('mpb-step-label');
    this._footer       = document.getElementById('mpb-footer');

    if (!this._container) {
      console.error('[MealPlanner] #mpb-container not found');
      return;
    }

    
    document.addEventListener('onDurationChange', this._onDurationChangeBound);

    this._container.innerHTML = '<div class="mpb-loading">Loading products…</div>';
    this.state.stepStates = this._buildStepStates();
    this.productCache     = await this._fetchAllProducts();
    this._buildMealItems();
    this._logMetafields();
    this._render();
    this._nudgeFloatingWidgets();
  }

 
  destroy() {
    document.removeEventListener('onDurationChange', this._onDurationChangeBound);
    this._destroyCards();
    this._widgetObserver?.disconnect();
  }

  changeDuration(weeks) {
    if (this.state.duration === weeks) return;
    this.state.duration = weeks;

    Object.values(this.mealItems).forEach(item => item.setDuration(weeks));

    document.dispatchEvent(new CustomEvent('onDurationChange', {
      bubbles: true,
      detail: {
        weeks,
        label: this.cfg.durationMap[weeks] || `${weeks} Week`,
      },
    }));
  }

  // Reacts to 'onDurationChange' — re-renders the current step so cards,
  // prices, qty sections, and sub-product sections all reflect the new duration.
  // _render() reads stepStates.selections before touching the DOM, so every
  // selection, diet pref, qty, and sub-product choice is fully preserved.
  _handleDurationChange(e) {
    this._render();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step state builder
  // ═══════════════════════════════════════════════════════════════════════════

  _buildStepStates() {
    return this.cfg.steps.map(stepCfg => {
      const modes = stepCfg.modes.map(m => ({
        key:       m.key,
        label:     m.label,
        subtitle:  m.subtitle || '',
        selection: m.selection,   // 'radio' | 'multi'
        handles:   (m.products || []).map(p => p.handle),
      }));

      const selections = {};
      modes.forEach(m => {
        selections[m.key] = (m.selection === 'radio') ? null : [];
      });

      return {
        category:       stepCfg.name,
        availableModes: modes,
        activeMode:     modes.length === 1 ? modes[0].key : null,
        selections,
      };
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Product fetching
  // ═══════════════════════════════════════════════════════════════════════════

  async _fetchAllProducts() {
    const handles = new Set();
    this.cfg.steps.forEach(s =>
      s.modes.forEach(m => (m.products || []).forEach(p => {
        handles.add(p.handle);
        (p.subProducts || []).forEach(sp => handles.add(sp.handle));
      }))
    );

    const cache = {};
    await Promise.all([...handles].map(async handle => {
      try {
        const r = await fetch(`/products/${handle}.js`);
        if (r.ok) cache[handle] = await r.json();
      } catch { /* card renders gracefully without product data */ }
    }));
    return cache;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MealItem pool  (one instance per step-handle pair)
  // Keyed as "${stepIdx}-${handle}" so the same product used in Breakfast and
  // Lunch gets independent dietaryPref / price state.
  // Product JSON is still shared via this.productCache (one fetch per handle).
  // ═══════════════════════════════════════════════════════════════════════════

  // Consistent key used everywhere to look up this.mealItems
  _itemKey(stepIdx, handle) { return `${stepIdx}-${handle}`; }

  _buildMealItems() {
    this.cfg.steps.forEach((stepCfg, stepIdx) => {
      stepCfg.modes.forEach(modeCfg => {
        (modeCfg.products || []).forEach(cfgProd => {
          const { handle } = cfgProd;
          const key = this._itemKey(stepIdx, handle);
          if (!this.mealItems[key]) {
            this.mealItems[key] = new MealItem({
              handle,
              product:       this.productCache[handle] || null,
              configProduct: cfgProd,
              duration:      this.state.duration,
              stepCategory:  stepCfg.name,
            });
          }
        });
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Totals & nutrition
  // ═══════════════════════════════════════════════════════════════════════════

  _handlesForStep(stepState) {
    const sel = stepState.selections[stepState.activeMode];
    return !sel ? [] : Array.isArray(sel) ? sel : [sel];
  }

  // Returns false when the item's price should be excluded from totals.
  // Gated on: (1) diet preference unset, (2) sub-product unset when sub-products exist.
  _shouldCountPrice(item) {
    if (!item) return false;
    if (item.hasDietOptions?.() && !item.dietaryPref) return false;
    if (item.configProd?.subProducts?.length && !item.selectedSubProduct) return false;
    return true;
  }

  // ── selectedItem — single source of truth ────────────────────────────────────
  // Called before every render AND before every in-place price update.
  // All grand-total helpers read from this.state.selectedItem, never re-iterate
  // stepStates themselves — one loop to rule them all.
  _rebuildSelectedItem() {
    const dur   = this.state.duration;
    const items = [];

    this.state.stepStates.forEach((step, stepIdx) => {
      const activeModeKey = step.activeMode;
      const modeConfig    = step.availableModes.find(m => m.key === activeModeKey);
      const sel           = step.selections[activeModeKey];
      const handles       = !sel ? [] : Array.isArray(sel) ? sel : [sel];

      handles.forEach(handle => {
        const item        = this.mealItems[this._itemKey(stepIdx, handle)];
        if (!item) return;
        const priceActive = this._shouldCountPrice(item);
        const subHandle   = item.selectedSubProduct || null;

        items.push({
          key:           `${stepIdx}-${activeModeKey}-${handle}`,
          handle,
          stepIdx,
          stepName:      step.category,
          modeKey:       activeModeKey,
          modeLabel:     modeConfig?.label || activeModeKey,   // e.g. "Complete Meal"
          title:         item.configProd?.title || item.configProd?.displayName || this.productCache[handle]?.title || handle,
          dietaryPref:   item.dietaryPref || null,
          needsDietPref: !!(item.hasDietOptions?.() && !item.dietaryPref),
          priceActive,                                         // false = awaiting diet pref
          weeklyPrice:   priceActive ? (item.getPrice(1)                  || 0) : 0,
          totalPrice:    priceActive ? this._computeItemPrice(item, dur)       : 0,
          savings:       priceActive ? this._computeItemSavings(item, dur)     : 0,
          selectedSubProduct:      subHandle,
          selectedSubProductLabel: (() => {
            if (!subHandle) return null;
            const sp = item.configProd?.subProducts?.find(s => s.handle === subHandle);
            return sp?.label || this.productCache[subHandle]?.title || null;
          })(),
          selectedQuantity: item.selectedQuantity || null,
          selectedDeliveryTime: item.selectedDeliveryTime || null,
          selectedDeliveryTimeLabel: (() => {
            if (!item.selectedDeliveryTime) return null;
            const retDt = item.getRetentionInfo?.()?.delivery_time;
            return retDt?.[item.selectedDeliveryTime] || item.selectedDeliveryTime;
          })(),
          flourPackets: (() => {
            if (!subHandle) return null;
            const sp = item.configProd?.subProducts?.find(s => s.handle === subHandle);
            if (sp?.variantSku === 'all-002-1-0') return this._diyFlourPackets(item, dur);
            return null;
          })(),
        });
      });
    });

    this.state.selectedItem = items;
    window.selectedItem     = items;   // expose for external scripts
  }

  // ── Grand-total helpers — all derive from this.state.selectedItem ─────────────

  _grandTotal() {
    return this.state.selectedItem.reduce((sum, i) => sum + i.totalPrice, 0);
  }

  _grandSavings() {
    return this.state.selectedItem.reduce((sum, i) => sum + i.savings, 0);
  }

  // Weekly equivalent price — shown in the summary price box.
  // Derived from the grand total divided by selected duration so it is always
  // accurate regardless of whether products have an explicit "1 Week" variant.
  _grandWeeklyTotal() {
    const dur   = this.state.duration || 1;
    const total = this._grandTotal();
    return dur <= 1 ? total : Math.round(total / dur);
  }

  // Savings for a *hypothetical* duration — used only to annotate duration buttons.
  // Cannot use selectedItem (which is priced at this.state.duration), so we
  // iterate stepStates directly with the requested week count.
  _savingsForDuration(weeks) {
    return this.state.stepStates.reduce((sum, step, stepIdx) =>
      sum + this._handlesForStep(step).reduce((s, h) => {
        const item = this.mealItems[this._itemKey(stepIdx, h)];
        if (!this._shouldCountPrice(item)) return s;
        return s + (item.getSavings(weeks) || 0);
      }, 0), 0);
  }

  // ── Sub-product overrides ─────────────────────────────────────────────────────
  // When the user picks "Roti Flour (DIY)" as their sub-product, price is driven
  // by how many flour packets they need (packets = ceil(qty × days / rotisPerPacket))
  // rather than the parent Roti product's variant price.
  // Mirrors nutripack.liquid's retentionData.savings * p.multiplier pattern.

  _computeItemPrice(item, dur) {
    if (item.selectedSubProduct === 'low-carb-lchf-roti-flour') {
      return this._diyFlourPrice(item, dur);
    }
    return item.getPrice(dur) || 0;
  }

  _computeItemSavings(item, dur) {
    if (item.selectedSubProduct === 'low-carb-lchf-roti-flour') return 0;
    return item.getSavings(dur) || 0;
  }

  // Number of 500 g flour packs needed for a given item + duration.
  // One pack makes ~15 rotis. Used by both price calc and cart quantity.
  _diyFlourPackets(item, dur) {
    const ROTIS_PER_PACKET = 15;
    const qty = item.selectedQuantity || 1;
    return Math.ceil((qty * dur * 7) / ROTIS_PER_PACKET);
  }

  // Returns total price for the required number of flour packets.
  // Per-packet price: "1 pack - 500 g" variant (SKU all-002-1-0, ₹600).
  // Falls back to ₹600 if the product is not in the cache yet.
  _diyFlourPrice(item, dur) {
    const FLOUR_HANDLE   = 'low-carb-lchf-roti-flour';
    const FALLBACK_PRICE = 600;   // ₹600 — "1 pack - 500 g" (SKU: all-002-1-0)

    const packetsNeeded = this._diyFlourPackets(item, dur);

    const flourProduct = this.productCache[FLOUR_HANDLE];
    let perPacketPrice  = FALLBACK_PRICE;

    if (flourProduct?.variants?.length) {
      const packVariant =
        flourProduct.variants.find(v =>
          v.title.toLowerCase().includes('1 pack') || v.sku === 'all-002-1-0'
        ) || flourProduct.variants[0];
      perPacketPrice = MealItem.parsePaise(packVariant.price);
    }

    return perPacketPrice * packetsNeeded;
  }

  _aggregateNutrition() {
    const totals = { protein: 0, carbs: 0, fiber: 0 };
    this.state.stepStates.forEach((step, stepIdx) =>
      this._handlesForStep(step).forEach(h => {
        const nut   = this.mealItems[this._itemKey(stepIdx, h)]?.getNutrition() || {};
        totals.protein += nut.protein || 0;
        totals.carbs   += nut.carbs   || 0;
        totals.fiber   += nut.fiber   || 0;
      })
    );
    return totals;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════

  _renderDurationSlot() {
    if (!this._durationSlot) return;
    this._durationSlot.innerHTML = '';
    const isSummary = this.state.currentStep >= this.state.stepStates.length;
    this._durationSlot.appendChild(
      isSummary ? this._buildDurationBadgeEl() : this._buildDurationSelectorEl()
    );
  }

  _render() {
    this._rebuildSelectedItem();
    this._destroyCards();
    this._renderDurationSlot();
    this._container.innerHTML = '';

    const isSummary = this.state.currentStep >= this.state.stepStates.length;
    this._container.appendChild(
      isSummary
        ? this._buildSummaryEl()
        : this._buildStepEl(
            this.state.stepStates[this.state.currentStep],
            this.state.currentStep
          )
    );

    this._syncIndicator();
    this._syncLabel();
    this._syncFooter();
  }

  _destroyCards() {
    Object.values(this.mealCards).forEach(c => c.destroy());
    this.mealCards = {};
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step builder
  // ═══════════════════════════════════════════════════════════════════════════

  _buildStepEl(stepState, stepIdx) {
    const wrap = document.createElement('div');
    wrap.className = 'mpb-step-wrapper';

    const stepCfgEntry = this.cfg.steps[stepIdx];

    // ── Gray header: ONLY title + mode toggle ────────────────────────────────
    const header = document.createElement('div');
    header.className = 'mpb-step-header';

    const title = document.createElement('h2');
    title.className   = 'mpb-category-title';
    title.textContent = stepCfgEntry?.title || `Choose your ${stepState.category}`;
    header.appendChild(title);

    if (stepState.availableModes.length > 1) {
      header.appendChild(this._buildModeToggleEl(stepState, stepIdx));
    }
    wrap.appendChild(header);

    // ── Subtitle + cards: BELOW the gray header, on the white container bg ──
    if (stepCfgEntry?.subtitle) {
      const sub = document.createElement('p');
      sub.className   = 'mpb-category-subtitle';
      sub.textContent = stepCfgEntry.subtitle;
      wrap.appendChild(sub);
    }

    wrap.appendChild(this._buildCardsGridEl(stepState, stepIdx));

    // Per-step flag takes precedence; falls back to global flag, then defaults to true
    const stepCfg   = this.cfg.steps[stepIdx];
    const showChart = stepCfg?.showNutritionChart !== undefined
      ? stepCfg.showNutritionChart
      : this.cfg.showNutritionChart !== false;

    if (showChart) {
      wrap.appendChild(this._buildNutritionEl());
    }

    return wrap;
  }

  // ── Interactive duration selector (Step 1 only) ─────────────────────────────

  _buildDurationSelectorEl() {
    const wrap = document.createElement('div');
    wrap.className = 'mpb-duration-section';

    const heading = document.createElement('h2');
    heading.className   = 'mpb-section-title';
    heading.textContent = 'Choose Duration (weeks)';
    wrap.appendChild(heading);

    const group = document.createElement('div');
    group.className = 'mpb-duration-group';
    group.id        = 'mpb-duration-group';

    Object.entries(this.cfg.durationMap).forEach(([k, v]) => {
      const weeks   = +k;
      const pct     = this.cfg.discounts?.[weeks] || 0;

      const btn = document.createElement('button');
      btn.type          = 'button';
      btn.className     = `mpb-duration-btn${weeks === this.state.duration ? ' selected' : ''}`;
      btn.dataset.weeks = k;

      btn.innerHTML = `<span class="mpb-dur-label">${weeks}</span>${pct > 0 ? `<span class="mpb-dur-pct-badge">Save ${pct}%</span>` : ''}`;
      btn.addEventListener('click', () => this.changeDuration(weeks));
      group.appendChild(btn);
    });

    wrap.appendChild(group);
    return wrap;
  }

  // ── Locked duration buttons (Steps 2+ and Summary) ──────────────────────────
  // Same layout as Step 1 but every non-selected button is disabled/grayed.
  _buildDurationBadgeEl() {
    const wrap = document.createElement('div');
    wrap.className = 'mpb-duration-section';

    const heading = document.createElement('h2');
    heading.className   = 'mpb-section-title';
    heading.textContent = 'Choose Duration (week)';
    wrap.appendChild(heading);

    const group = document.createElement('div');
    group.className = 'mpb-duration-group';

    Object.entries(this.cfg.durationMap).forEach(([k, v]) => {
      const weeks      = +k;
      const isSelected = weeks === this.state.duration;
      const pct        = this.cfg.discounts?.[weeks] || 0;

      const btn = document.createElement('button');
      btn.type      = 'button';
      btn.className = `mpb-duration-btn${isSelected ? ' selected' : ' locked'}`;
      btn.dataset.weeks = k;
      btn.disabled  = !isSelected;

      btn.innerHTML = `<span class="mpb-dur-label">${weeks}</span>${pct > 0 ? `<span class="mpb-dur-pct-badge">Save ${pct}%</span>` : ''}`;
      group.appendChild(btn);
    });

    wrap.appendChild(group);
    return wrap;
  }

  // ── Read-only duration buttons (Steps 2+) ───────────────────────────────────
  // Renders the same 4-button row as Step 1, but:
  //   • The button matching this.state.duration keeps the 'selected' highlight.
  //   • Every other button has the 'locked' class + disabled attr — grayed out,
  //     no pointer, no click — purely visual reference for what was chosen.



  // ── Mode selector cards (Complete Meal / Add-ons) ───────────────────────────

  _buildModeToggleEl(stepState, stepIdx) {
    const wrap = document.createElement('div');
    wrap.className = 'mpb-mode-cards';

    stepState.availableModes.forEach(m => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'mpb-mode-card' + (stepState.activeMode === m.key ? ' selected' : '');
      card.dataset.modeKey = m.key;
      card.innerHTML = `
        <span class="mpb-mode-card-label">${m.label}</span>
        ${m.subtitle ? `<span class="mpb-mode-card-sub">${m.subtitle}</span>` : ''}`;

      card.addEventListener('click', () => {
        // Toggle: clicking the active mode deselects it
        stepState.activeMode = stepState.activeMode === m.key ? null : m.key;
        this._rebuildSelectedItem();
        this._render();
      });

      wrap.appendChild(card);
    });

    return wrap;
  }

  // ── Cards grid ───────────────────────────────────────────────────────────────

  _buildCardsGridEl(stepState, stepIdx) {
    // No mode chosen yet — user must click a mode card first
    if (!stepState.activeMode) return document.createDocumentFragment();

    const mode = stepState.availableModes.find(m => m.key === stepState.activeMode);

    if (!mode || !mode.handles.length) {
      const empty = document.createElement('div');
      empty.className   = 'mpb-empty-state';
      empty.textContent = 'No products configured for this step yet.';
      return empty;
    }

    const sel      = stepState.selections[stepState.activeMode];
    const selected = !sel ? [] : Array.isArray(sel) ? sel : [sel];

    const grid = document.createElement('div');
    grid.className = 'mpb-cards-grid';

    mode.handles.forEach(handle => {
      const mealItem = this.mealItems[this._itemKey(stepIdx, handle)];
      if (!mealItem) return;

      const cardKey = `${stepIdx}-${mode.key}-${handle}`;
      const card    = new MealCard({
        mealItem,
        mode:                mode.selection,
        isSelected:          selected.includes(handle),
        onToggle:            (h, nowSelected) =>
          this._handleCardToggle(stepState, stepIdx, h, nowSelected, mode),
        onDietChange:        () => this._render(),
        onQuantityChange:    () => this._render(),
        resolvedSubProducts: this._resolveSubProducts(mealItem.configProd),
      });

      this.mealCards[cardKey] = card;
      grid.appendChild(card.render());
    });

    return grid;
  }

  // Resolves sub-product config entries into display-ready objects using productCache + metafields.
  _resolveSubProducts(configProd) {
    if (!configProd?.subProducts?.length) return [];
    return configProd.subProducts.map(sp => {
      const prod      = this.productCache[sp.handle];
      const imgSrc    = prod?.featured_image || prod?.images?.[0]?.src || '';
      const bodyTxt   = sp.description
        || window.MPB_METAFIELDS?.[sp.handle]?.metafields?.short_description
        || (prod?.body_html ? prod.body_html.replace(/<[^>]+>/g, '').trim() : '');
      const _parseDetails = raw => {
        if (!raw) return null;
        if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
        return raw;
      };
      const details      = _parseDetails(window.MPB_METAFIELDS?.[sp.handle]?.metafields?.product_details);
      const rawPrice     = details?.per_meal_price;
      const perMealPrice = (rawPrice !== undefined && rawPrice !== null && rawPrice !== '')
        ? parseFloat(rawPrice) : null;
      const moreInfo     = details?.more_info || null;
      return {
        handle:      sp.handle,
        label:       sp.label || prod?.title || sp.handle,
        imgSrc,
        bodyTxt,
        perMealPrice,
        configPrice: sp.price || null,
        delivery:    sp.delivery || null,
        tags:        sp.tags || [],
        moreInfo,
        showInfo:    sp.showInfo || false,
      };
    });
  }

  _handleCardToggle(stepState, stepIdx, handle, nowSelected, mode) {
    if (mode.selection === 'radio') {
      const prev = stepState.selections[stepState.activeMode];
      if (prev && prev !== handle) {
        this.mealItems[this._itemKey(stepIdx, prev)]?.clearDietaryPref();
      }
      stepState.selections[stepState.activeMode] = nowSelected ? handle : null;
    } else {
      const arr = stepState.selections[stepState.activeMode];
      const idx = arr.indexOf(handle);
      if (nowSelected && idx < 0) {
        arr.push(handle);
        this.mealItems[this._itemKey(stepIdx, handle)]?.clearDietaryPref();
      } else if (!nowSelected && idx >= 0) {
        arr.splice(idx, 1);
        const item = this.mealItems[this._itemKey(stepIdx, handle)];
        item?.clearDietaryPref();
        item?.clearQuantity();
      }
    }

    // Auto-apply defaultDeliveryTime from config when a card is selected.
    // This is the mechanism behind "protein smoothie → morning in Breakfast,
    // evening in Snacks" — set configProd.defaultDeliveryTime in meal-plan-config.js.
    // Direct assignment (not setDeliveryTime) avoids the toggle-off side-effect.
    if (nowSelected) {
      const item = this.mealItems[this._itemKey(stepIdx, handle)];
      const defaultDt = item?.configProd?.defaultDeliveryTime;
      if (item && defaultDt) {
        item.selectedDeliveryTime = defaultDt;
      }
    }

    this._render();
  }

  // ── Price bar ────────────────────────────────────────────────────────────────

  _buildPriceBarEl() {
    const weeks     = this.state.duration;
    const weekLabel = this.cfg.durationMap[weeks] || `${weeks} Week`;
    const total     = this._grandTotal();
    const savings   = this._grandSavings();
    const nextIdx   = this.state.currentStep + 1;
    const nextLabel = nextIdx >= this.state.stepStates.length
      ? 'Review Plan'
      : this.state.stepStates[nextIdx].category;

    const savingsHtml = savings > 0
      ? `<span class="mpb-price-bar-save" id="mpb-price-bar-save">Save ₹${savings.toLocaleString('en-IN')}</span>`
      : '';

    const bar = document.createElement('div');
    bar.className = 'mpb-price-bar';
    bar.id        = 'mpb-price-bar';
    bar.innerHTML = `
      <div class="mpb-price-bar-info">
        <span class="mpb-price-bar-label" id="mpb-price-bar-label">
          Total for ${weekLabel}
        </span>
        <div class="mpb-price-bar-amount-row">
          <span class="mpb-price-bar-amount" id="mpb-price-bar-amount">
            ${total > 0 ? '₹' + total.toLocaleString('en-IN') : '—'}
          </span>
          ${savingsHtml}
        </div>
      </div>
      <button class="mpb-btn-next" id="mpb-btn-next-bar" type="button">
        Next: ${nextLabel}
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 8h10M9 4l4 4-4 4"/>
        </svg>
      </button>`;

    bar.querySelector('#mpb-btn-next-bar').addEventListener('click', () => this._nextStep());
    return bar;
  }

  // In-place update — called via onDurationChange listener (no full re-render).
  // selectedItem is already rebuilt by _updateFooterAmount() earlier in the same tick.
  _updatePriceBarDisplay() {
    const total     = this._grandTotal();
    const savings   = this._grandSavings();
    const weeks     = this.state.duration;
    const weekLabel = this.cfg.durationMap[weeks] || `${weeks} Week`;

    const labelEl  = document.getElementById('mpb-price-bar-label');
    const amountEl = document.getElementById('mpb-price-bar-amount');
    let   saveEl   = document.getElementById('mpb-price-bar-save');

    if (labelEl)  labelEl.textContent  = `Total for ${weekLabel}`;
    if (amountEl) amountEl.textContent = total > 0 ? `₹${total.toLocaleString('en-IN')}` : '—';

    const row = amountEl?.parentElement;
    if (row) {
      if (savings > 0) {
        if (!saveEl) {
          saveEl = document.createElement('span');
          saveEl.className = 'mpb-price-bar-save';
          saveEl.id        = 'mpb-price-bar-save';
          row.appendChild(saveEl);
        }
        saveEl.textContent = `Save ₹${savings.toLocaleString('en-IN')}`;
      } else if (saveEl) {
        saveEl.remove();
      }
    }
  }

  // In-place update of the interactive duration button active states (Step 1)
  _updateDurationButtons() {
    document.querySelectorAll('.mpb-duration-btn').forEach(btn => {
      const weeks = +btn.dataset.weeks;
      btn.classList.toggle('selected', weeks === this.state.duration);
    });
  }

  // ── Sticky footer ─────────────────────────────────────────────────────────

  _syncFooter() {
    if (!this._footer) return;

    const isSummary = this.state.currentStep >= this.state.stepStates.length;
    const weeks     = this.state.duration;
    const weekLabel = this.cfg.durationMap[weeks] || `${weeks} Week`;
    const total     = this._grandTotal();
    const isFirst   = this.state.currentStep === 0;

    let nextLabel;
    if (isSummary) {
      nextLabel = 'Add to Cart';
    } else {
      const nextIdx = this.state.currentStep + 1;
      nextLabel = nextIdx >= this.state.stepStates.length
        ? 'Review Plan'
        : this.state.stepStates[nextIdx].category;
    }

    const savings     = this._grandSavings();
    const savingsHtml = savings > 0 && this.state.duration >= 4
      ? `<span class="mpb-footer-save" id="mpb-footer-save">Save ₹${savings.toLocaleString('en-IN')}</span>`
      : '';

    this._footer.innerHTML = `
      <div class="mpb-footer-inner">
        <div class="mpb-footer-left">
          <span class="mpb-footer-label" id="mpb-footer-label">Total for ${weekLabel}</span>
          <div class="mpb-footer-amount-row">
            <span class="mpb-footer-amount" id="mpb-footer-amount">${total > 0 ? '₹' + total.toLocaleString('en-IN') : '—'}</span>
            ${savingsHtml}
          </div>
        </div>
        <div class="mpb-footer-right">
          ${!isFirst ? `<button class="mpb-btn-back" id="mpb-footer-back" type="button">← Back</button>` : ''}
          <button class="mpb-btn-next" id="mpb-footer-next" type="button">
            ${isSummary ? 'Add to Cart' : 'Next: ' + nextLabel}
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 8h10M9 4l4 4-4 4"/>
            </svg>
          </button>
        </div>
      </div>`;

    if (!isFirst) {
      this._footer.querySelector('#mpb-footer-back')
        .addEventListener('click', () => this._prevStep());
    }

    this._footer.querySelector('#mpb-footer-next')
      .addEventListener('click', () => {
        if (isSummary) this._addToCart();
        else           this._nextStep();
      });
  }

  // In-place price update in footer (avoids full footer re-render on duration change).
  // Must rebuild selectedItem first so totals reflect the new duration.
  _updateFooterAmount() {
    this._rebuildSelectedItem();          // keep selectedItem in sync with new duration
    const total     = this._grandTotal();
    const savings   = this._grandSavings();
    const weeks     = this.state.duration;
    const weekLabel = this.cfg.durationMap[weeks] || `${weeks} Week`;

    const labelEl  = document.getElementById('mpb-footer-label');
    const amountEl = document.getElementById('mpb-footer-amount');
    let   saveEl   = document.getElementById('mpb-footer-save');

    if (labelEl)  labelEl.textContent  = `Total for ${weekLabel}`;
    if (amountEl) amountEl.textContent = total > 0 ? `₹${total.toLocaleString('en-IN')}` : '—';

    const row = amountEl?.parentElement;
    if (row) {
      if (savings > 0 && this.state.duration >= 4) {
        if (!saveEl) {
          saveEl = document.createElement('span');
          saveEl.className = 'mpb-footer-save';
          saveEl.id        = 'mpb-footer-save';
          row.appendChild(saveEl);
        }
        saveEl.textContent = `Save ₹${savings.toLocaleString('en-IN')}`;
      } else if (saveEl) {
        saveEl.remove();
      }
    }
  }

  // Weekly equivalent price — shown in the summary price box.
  // Derived from the grand total ÷ selected duration so it is always accurate
  // regardless of whether products expose an explicit "1 Week" variant title.
  _grandWeeklyTotal() {
    const dur   = this.state.duration || 1;
    const total = this._grandTotal();
    return dur <= 1 ? total : Math.round(total / dur);
  }

  // ── Nutrition chart ──────────────────────────────────────────────────────────

  _buildNutritionEl() {
    const nut = this._aggregateNutrition();
    const nt  = this.cfg.nutritionTargets || {};

    const macros = [
      { key: 'protein',  label: nt.protein?.label  || 'Protein',
        current: nut.protein, target: nt.protein?.target  || 125,
        unit: 'g', maxScale: nt.protein?.maxScale  || 150 },
      { key: 'carbs',    label: nt.carbs?.label    || 'Carbs',
        current: nut.carbs,   target: nt.carbs?.target    || 125,
        unit: 'g', maxScale: nt.carbs?.maxScale    || 150 },
      { key: 'fiber',    label: nt.fiber?.label    || 'Fiber',
        current: nut.fiber,   target: nt.fiber?.target    || 55,
        unit: 'g', maxScale: nt.fiber?.maxScale    || 150 },
      { key: 'vitamins', label: nt.vitamins?.label || 'Vitamins\u00A0&\u00A0Minerals',
        current: null,        target: null,
        unit: '',  maxScale: 150 },
    ];

    const zoneColor = (cur, tgt) => {
      if (tgt == null || cur == null) return '#EF4444';
      const pct = cur / tgt;
      if (pct >= 1.0)  return '#22C55E';
      if (pct >= 0.66) return '#EAB308';
      if (pct >= 0.33) return '#F97316';
      return '#EF4444';
    };

    const barRows = macros.map(m => {
      const fillPct   = m.current === null ? 5 : Math.min(m.current / m.maxScale * 100, 100);
      const valLabel  = m.current === null ? 'Low' : `${m.current}${m.unit}`;
      const rightClip = (100 - fillPct).toFixed(2);
      return `
        <div class="mpb-nutrition-row">
          <div class="mpb-nutrition-label">${m.label}</div>
          <div class="mpb-chart-wrap">
            <div class="mpb-chart-gridlines">
              <div class="mpb-grid-section"></div>
              <div class="mpb-grid-section"></div>
              <div class="mpb-grid-section mpb-grid-last"></div>
            </div>
            <div class="mpb-nutrition-bar-track">
              <div class="mpb-nutrition-bar-fill"
                   style="clip-path:inset(0 ${rightClip}% 0 0 round 4px)"></div>
            </div>
          </div>
          <div class="mpb-nutrition-value-label">${valLabel}</div>
        </div>`;
    }).join('');

    const legendItems = macros.map(m => {
      const cur  = m.current !== null ? `${m.current}${m.unit}` : 'Low';
      const dot  = zoneColor(m.current !== null ? m.current : 0, m.target);
      const tLine = m.target
        ? `<span class="mpb-legend-target">Target: ${m.target}${m.unit}</span>` : '';
      return `
        <div class="mpb-legend-item">
          <span class="mpb-legend-dot" style="background:${dot}"></span>
          <div class="mpb-legend-text">
            <span class="mpb-legend-name">${m.label}</span>
            <span class="mpb-legend-current">Current: <strong>${cur}</strong></span>
            ${tLine}
          </div>
        </div>`;
    }).join('');

    const wrap = document.createElement('div');
    wrap.className = 'mpb-nutrition';
    wrap.innerHTML = `
      <div class="mpb-nutrition-title">Daily Macro-nutrients from your selection</div>
      <div class="mpb-nutrition-scale-row">
        <div class="mpb-nutrition-label-spacer"></div>
        <div class="mpb-nutrition-scale-track">
          <span>0</span><span>50</span><span>100</span><span>150</span>
        </div>
        <div class="mpb-nutrition-val-spacer"></div>
      </div>
      <div class="mpb-nutrition-bars">${barRows}</div>
      <div class="mpb-nutrition-legend">${legendItems}</div>
      <div class="mpb-color-guide">
        <span class="mpb-color-guide-title">Color Guide:</span>
        <div class="mpb-color-guide-items">
          <div class="mpb-cg-item"><span class="mpb-cg-dot" style="background:#EF4444"></span>0–33% of target</div>
          <div class="mpb-cg-item"><span class="mpb-cg-dot" style="background:#F97316"></span>33–66%</div>
          <div class="mpb-cg-item"><span class="mpb-cg-dot" style="background:#EAB308"></span>66–99%</div>
          <div class="mpb-cg-item"><span class="mpb-cg-dot" style="background:#22C55E"></span>100%+ target</div>
        </div>
      </div>`;

    return wrap;
  }

  // ── Bottom nav ────────────────────────────────────────────────────────────────

  _buildBottomNavEl() {
    const nextIdx   = this.state.currentStep + 1;
    const nextLabel = nextIdx >= this.state.stepStates.length
      ? 'Review Plan'
      : this.state.stepStates[nextIdx].category;
    const isFirst   = this.state.currentStep === 0;

    const wrap = document.createElement('div');
    wrap.className = 'mpb-bottom-nav';

    if (!isFirst) {
      const backBtn = document.createElement('button');
      backBtn.type        = 'button';
      backBtn.className   = 'mpb-btn-back';
      backBtn.textContent = '← Back';
      backBtn.addEventListener('click', () => this._prevStep());
      wrap.appendChild(backBtn);
    } else {
      wrap.appendChild(document.createElement('div')); // spacer
    }

    const nextBtn = document.createElement('button');
    nextBtn.type      = 'button';
    nextBtn.className = 'mpb-btn-next';

    nextBtn.innerHTML = `Next: ${nextLabel}
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 8h10M9 4l4 4-4 4"/>
      </svg>`;
    nextBtn.addEventListener('click', () => this._nextStep());
    wrap.appendChild(nextBtn);

    return wrap;
  }

  // ── Summary (Step 5) ──────────────────────────────────────────────────────────
  // Layout:
  //   "Review Your Selections"  heading
  //   "Here's what you've chosen…" subtitle
  //   [Edit Your Selections] blue filled button  ← goes to Step 1
  //   ┌─ Suggested Products card (cream bg) ─────────────────────────────┐
  //   │  Product Name              ₹[total price for duration]           │
  //   │  Unit Price: ₹X/week       for [duration label]                  │
  //   └──────────────────────────────────────────────────────────────────┘
  //   ─────────────────────────────────────────────────────────────────────
  //   Total                                         ₹[grand total]
  //   [Add to Cart] full-width blue button

  _buildSummaryEl() {
    const weeks       = this.state.duration;
    const weekLabel   = this.cfg.durationMap[weeks] || `${weeks} Week`;
    const total       = this._grandTotal();
    const weeklyTotal = this._grandWeeklyTotal();
    const savings     = this._grandSavings();
    const showSavings = savings > 0 && weeks >= 4;

    const wrap = document.createElement('div');

    // Review header + Edit button
    const headerDiv = document.createElement('div');
    headerDiv.className = 'mpb-review-header';
    headerDiv.innerHTML = `
      <h2 class="mpb-review-title">Review Your Selections</h2>
      <p class="mpb-review-subtitle">Here's what you've chosen for your meal plan</p>
      <button class="mpb-btn-edit-selections" id="mpb-btn-edit" type="button">
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793z"/>
          <path d="M11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
        </svg>
        Edit Your Selections
      </button>`;
    wrap.appendChild(headerDiv);

    // ── Category cards (one per step, in order) ─────────────────────────────
    const categoriesEl = document.createElement('div');
    categoriesEl.className = 'mpb-summary-categories';

    this.state.stepStates.forEach((step, stepIdx) => {
      // Items for this step from the global selectedItem list
      const stepItems = this.state.selectedItem.filter(i => i.stepIdx === stepIdx);

      // Skip categories where nothing was selected
      if (!stepItems.length) return;

      const catEl = document.createElement('div');
      catEl.className = 'mpb-summary-cat';

      // Header
      const catHead = document.createElement('div');
      catHead.className = 'mpb-summary-cat-header';
      catHead.innerHTML = `<span class="mpb-summary-cat-name">${step.category}</span>`;
      catEl.appendChild(catHead);

      // Body
      const catBody = document.createElement('div');
      catBody.className = 'mpb-summary-cat-body';

      stepItems.forEach(item => {
          const row = document.createElement('div');
          row.className = 'mpb-summary-item-row';

          // Diet pill
          let dietHtml = '';
          if (item.dietaryPref) {
            const slug = item.dietaryPref.toLowerCase().replace(/[\s/]+/g, '-');
            dietHtml = `<span class="mpb-summary-diet-pill mpb-sdiet-${slug}">${item.dietaryPref}</span>`;
          } else if (item.needsDietPref) {
            dietHtml = `<span class="mpb-summary-diet-pill mpb-sdiet-missing">⚠ Select preference</span>`;
          }

          // Price
          const priceHtml = item.totalPrice > 0
            ? `<span class="mpb-summary-item-price">₹${item.totalPrice.toLocaleString('en-IN')}</span>
               <span class="mpb-summary-item-for">for ${weekLabel.toLowerCase()}</span>`
            : `<span class="mpb-summary-item-price mpb-summary-item-price--na">—</span>`;

          const subChoiceHtml = item.selectedSubProductLabel
            ? `<span class="mpb-summary-sub-choice"><span class="mpb-summary-sub-choice-label">Option:</span> ${item.selectedSubProductLabel}</span>`
            : '';
          const qtyHtml = item.selectedQuantity
            ? `<span class="mpb-summary-qty-info">${item.selectedQuantity}× per day</span>`
            : '';

          // Delivery slot — set automatically via configProd.defaultDeliveryTime on card
          // select (or by user chip choice if the delivery UI is re-enabled).
          // selectedDeliveryTimeLabel is always fresh because _rebuildSelectedItem()
          // runs before every render and reads the live MealItem state.
          const dtLabel = item.selectedDeliveryTimeLabel || null;
          const dtHtml  = dtLabel
            ? `<span class="mpb-summary-qty-info"><span class="mpb-summary-sub-choice-label">Delivery:</span> ${dtLabel}</span>`
            : '';
          const flourHtml = item.flourPackets
            ? `<span class="mpb-summary-qty-info mpb-summary-flour-qty">Qty: ${item.flourPackets}</span>`
            : '';

          row.innerHTML = `
            <div class="mpb-summary-row-info">
              <span class="mpb-summary-mode-badge">${item.modeLabel}</span>
              <span class="mpb-summary-item-title">${item.title}</span>
              ${subChoiceHtml}
              ${qtyHtml}
              ${dtHtml}
              ${flourHtml}
              ${dietHtml}
            </div>
            <div class="mpb-summary-row-price">
              ${priceHtml}
            </div>`;
          catBody.appendChild(row);
      });

      catEl.appendChild(catBody);
      categoriesEl.appendChild(catEl);
    });

    wrap.appendChild(categoriesEl);

    // Nutrition chart (config-gated)
    if (this.cfg.showNutritionChart !== false) {
      wrap.appendChild(this._buildNutritionEl());
    }

    // Price summary box
    const savingsBlockHtml = showSavings
      ? `<div class="mpb-price-sum-divider"></div>
         <div class="mpb-price-sum-section mpb-price-sum-section--savings">
           <span class="mpb-price-sum-label">You're saving</span>
           <span class="mpb-price-sum-savings">₹${savings.toLocaleString('en-IN')}</span>
         </div>`
      : '';

    const priceBox = document.createElement('div');
    priceBox.className = 'mpb-price-summary-box';
    priceBox.innerHTML = `
      <div class="mpb-price-sum-section">
        <span class="mpb-price-sum-label">Total for ${weekLabel}</span>
        <div class="mpb-price-sum-total-row">
          <span class="mpb-price-sum-total">₹${total.toLocaleString('en-IN')}</span>
          ${showSavings ? `<span class="mpb-sum-save-badge">Save ₹${savings.toLocaleString('en-IN')}</span>` : ''}
        </div>
      </div>
      ${savingsBlockHtml}`;
    wrap.appendChild(priceBox);

    wrap.querySelector('#mpb-btn-edit').addEventListener('click', () => this._goToStep(0));
    return wrap;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Navigation
  // ═══════════════════════════════════════════════════════════════════════════

  _nextStep() {
    // Validate that every selected card with diet options has a preference chosen
    if (!this._validateDietPrefs()) return;
    // Validate that every selected add-on card has qty + sub-option chosen
    if (!this._validateAddonSelections()) return;

    const next          = this.state.currentStep + 1;
    const isSummaryNext = next >= this.state.stepStates.length;

    // Selections on all steps are always preserved — navigating back and then
    // forward must restore exactly what the user previously chose.
    if (isSummaryNext) this.state.isEditing = false;

    this.state.visitedSteps.add(next);
    this.state.currentStep = next;
    this._render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Diet-preference validation ───────────────────────────────────────────────
  // Returns true if all selected items on the current step have a pref chosen.
  // On failure, highlights the offending cards and renders an inline alert.
  _validateDietPrefs() {
    const isSummary = this.state.currentStep >= this.state.stepStates.length;
    if (isSummary) return true;

    const stepIdx   = this.state.currentStep;
    const stepState = this.state.stepStates[stepIdx];
    const handles   = this._handlesForStep(stepState);
    const missing   = handles.filter(h => {
      const item = this.mealItems[this._itemKey(stepIdx, h)];
      return item?.hasDietOptions?.() && !item.dietaryPref;
    });

    if (missing.length === 0) {
      this._clearDietAlerts();
      return true;
    }

    // Highlight offending cards
    missing.forEach(handle => {
      const cardEl = this._container.querySelector(`[data-handle="${handle}"]`);
      if (cardEl) cardEl.classList.add('mpb-card-diet-required');
    });

    // Show inline alert (only once)
    let alertEl = this._container.querySelector('.mpb-diet-alert');
    if (!alertEl) {
      const nameList = missing
        .map(h => this.productCache[h]?.title || h)
        .join(', ');

      alertEl = document.createElement('div');
      alertEl.className = 'mpb-diet-alert';
      // Two explicit div children so flex never fragments inline text nodes
      alertEl.innerHTML = `
        <div class="mpb-diet-alert-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
            <path fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213
                 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1
                 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="mpb-diet-alert-text">
          Please pick <strong>Veg / Egg / Non-Veg</strong> for
          <strong>${nameList}</strong> before continuing.
        </div>`;

      // Insert just before the nutrition chart, or at the end of the wrapper
      const wrapper = this._container.querySelector('.mpb-step-wrapper');
      const chartEl = wrapper?.querySelector('.mpb-nutrition');
      if (wrapper) {
        chartEl ? wrapper.insertBefore(alertEl, chartEl) : wrapper.appendChild(alertEl);
      }
    }

    // Scroll the alert into view (especially helpful on mobile)
    requestAnimationFrame(() => {
      alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    return false;
  }

  _clearDietAlerts() {
    this._container?.querySelectorAll('.mpb-card-diet-required')
      .forEach(el => el.classList.remove('mpb-card-diet-required'));
    this._container?.querySelectorAll('.mpb-diet-alert, .mpb-addon-alert')
      .forEach(el => el.remove());
  }

  // ── Add-on / sub-option validation ─────────────────────────────────────────
  // Blocks navigation if ANY selected card (any mode) has an unfilled qty or
  // sub-option — works for both radio (Complete Meal) and multi (Add-ons).
  _validateAddonSelections() {
    const isSummary = this.state.currentStep >= this.state.stepStates.length;
    if (isSummary) return true;

    const stepIdx   = this.state.currentStep;
    const stepState = this.state.stepStates[stepIdx];
    const activeModeKey = stepState.activeMode;
    if (!activeModeKey) return true;

    const sel     = stepState.selections[activeModeKey];
    const handles = !sel ? [] : Array.isArray(sel) ? sel : [sel];

    const missingQty = [];
    const missingSub = [];
    const missingDt  = [];

    handles.forEach(handle => {
      const item = this.mealItems[this._itemKey(stepIdx, handle)];
      if (!item) return;

      // Check if qty options exist for this card
      const retInfo = item.getRetentionInfo?.();
      const retQty  = retInfo?.quantity_multiplier;
      const cfgQty  = item.configProd?.quantityOptions;
      const hasQtyOptions = (retQty && Object.keys(retQty).length) || cfgQty?.length;

      if (hasQtyOptions && !item.selectedQuantity) {
        missingQty.push(handle);
        return;   // sub-option depends on qty — report qty first, check sub later
      }

      // Check if a sub-product must be chosen (independent of whether qty was required)
      const subProds = item.configProd?.subProducts;
      if (subProds?.length && !item.selectedSubProduct) {
        missingSub.push(handle);
      }

      // [DELIVERY-UI HIDDEN] Delivery-time validation disabled — default is
      // auto-applied via configProd.defaultDeliveryTime on card select.
      // Re-enable by uncommenting the block below.
      /* [DELIVERY-UI HIDDEN]
      const retDt = retInfo?.delivery_time;
      if (retDt && Object.keys(retDt).length && !item.selectedDeliveryTime) {
        missingDt.push(handle);
      }
      [END DELIVERY-UI HIDDEN] */
    });

    if (missingQty.length === 0 && missingSub.length === 0 && missingDt.length === 0) {
      this._clearAddonAlerts();
      return true;
    }

    // Highlight offending cards
    [...missingQty, ...missingSub, ...missingDt].forEach(handle => {
      const cardEl = this._container.querySelector(`[data-handle="${handle}"]`);
      if (cardEl) cardEl.classList.add('mpb-card-diet-required');
    });

    // Show inline alert (only once)
    let alertEl = this._container.querySelector('.mpb-addon-alert');
    if (!alertEl) {
      let message = '';
      if (missingQty.length) {
        const names = missingQty
          .map(h => this.productCache[h]?.title || h).join(', ');
        message += `Please select a <strong>quantity</strong> for <strong>${names}</strong>.`;
      }
      if (missingSub.length) {
        const names = missingSub
          .map(h => this.productCache[h]?.title || h).join(', ');
        message += (message ? ' ' : '') +
          `Please choose a <strong>sub-option</strong> for <strong>${names}</strong>.`;
      }
      // [DELIVERY-UI HIDDEN] Delivery-time alert message disabled.
      /* [DELIVERY-UI HIDDEN]
      if (missingDt.length) {
        const names = missingDt
          .map(h => this.productCache[h]?.title || h).join(', ');
        message += (message ? ' ' : '') +
          `Please choose a <strong>delivery time</strong> for <strong>${names}</strong>.`;
      }
      [END DELIVERY-UI HIDDEN] */

      alertEl = document.createElement('div');
      alertEl.className = 'mpb-diet-alert mpb-addon-alert';
      alertEl.innerHTML = `
        <div class="mpb-diet-alert-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
            <path fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213
                 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1
                 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="mpb-diet-alert-text">${message}</div>`;

      const wrapper = this._container.querySelector('.mpb-step-wrapper');
      const chartEl = wrapper?.querySelector('.mpb-nutrition');
      if (wrapper) {
        chartEl ? wrapper.insertBefore(alertEl, chartEl) : wrapper.appendChild(alertEl);
      }
    }

    requestAnimationFrame(() => {
      alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    return false;
  }

  _clearAddonAlerts() {
    this._container?.querySelectorAll('.mpb-card-diet-required')
      .forEach(el => el.classList.remove('mpb-card-diet-required'));
    this._container?.querySelectorAll('.mpb-addon-alert')
      .forEach(el => el.remove());
  }

  _prevStep() {
    if (this.state.currentStep > 0) {
      this.state.currentStep--;
      this._render();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Jump to any step — called by "Edit Your Selections"; preserves ALL selections.
  // isEditing = true prevents _nextStep() from wiping the destination step's selections,
  // so every card stays highlighted and every dietaryPref stays intact as the user
  // navigates forward through the steps.
  _goToStep(idx) {
    this.state.isEditing   = true;
    this.state.currentStep = Math.max(0, idx);
    this._rebuildSelectedItem();   // keep global state fresh before re-render
    this._render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Shopify cart
  // ═══════════════════════════════════════════════════════════════════════════

  async _addToCart() {
    const items = [];
    this.state.stepStates.forEach((step, stepIdx) => {
      this._handlesForStep(step).forEach(handle => {
        const mealItem = this.mealItems[this._itemKey(stepIdx, handle)];
        if (!mealItem) return;
        // If a different sub-product handle is selected, pass its product data
        // so toCartItem() uses that variant instead of the parent product's
        const subHandle = mealItem.selectedSubProduct;
        let subProductData = null;
        let variantSku = null;
        let subQty = null;
        if (subHandle && subHandle !== handle) {
          subProductData = this.productCache[subHandle] || null;
          // Look up the sub-product config for an explicit variant SKU pin
          const subCfg = mealItem.configProd?.subProducts?.find(sp => sp.handle === subHandle);
          variantSku = subCfg?.variantSku || null;
          // For SKU-pinned flour packs: cart quantity = packets needed (mirrors price calc)
          if (variantSku === 'all-002-1-0') {
            subQty = this._diyFlourPackets(mealItem, this.state.duration);
          }
        }
        const cartItem = mealItem.toCartItem(this.state.duration, subProductData, variantSku, subQty);
        if (cartItem) items.push(cartItem);
      });
    });
    if (!items.length) return;

    const btn = document.getElementById('mpb-footer-next');
    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

    // Stamp (or clear) the hidden cart attribute so Shopify Flow / cart-drawer
    // can add the "Nutri-Pack" order tag only when checkout originates from the
    // nutripack page.  We always write the attribute — empty string on any other
    // page — so a tag set in a previous session visit cannot carry over.
    // This runs silently; a failure here must never block the actual cart add.
    try {
      const isNutriPackPage = window.location.pathname.includes('/pages/new-nutripack-page');
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          attributes: { '_order_tag': isNutriPackPage ? 'Nutri-Pack' : '' },
        }),
      });
    } catch (_) { /* non-critical */ }

    try {
      await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items }),
      }).then(r => r.json());

      // Attempt to open Dawn cart drawer if present.
      // Open the drawer immediately — don't wait on renderContents, which can
      // throw on some Dawn versions and would leave drawerOpened = false.
      let drawerOpened = false;
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        drawerOpened = true;                     // commit BEFORE any async work
        cartDrawer.classList.remove('is-empty');
        cartDrawer.open();

        // Refresh drawer contents in the background — a failure here must NOT
        // flip drawerOpened back or trigger the /cart redirect.
        fetch('/?sections=cart-drawer,cart-icon-bubble')
          .then(r => r.json())
          .then(sections => {
            if (typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents({ sections });
            }
          })
          .catch(() => { /* non-critical — drawer is already open */ });
      }

      if (btn) { btn.textContent = '✓ Added!'; btn.style.background = '#16A34A'; }

      if (drawerOpened) {
        // Drawer is open — reset button after a moment so user can keep browsing
        setTimeout(() => {
          if (btn) {
            btn.textContent = 'Add to Cart';
            btn.style.background = '';
            btn.disabled = false;
          }
        }, 2000);
      } else {
        // No cart drawer found — fall back to the /cart page
        setTimeout(() => { window.location.href = '/cart'; }, 1400);
      }

    } catch (err) {
      console.error('[MealPlanner] cart error:', err);
      if (btn) { btn.textContent = 'Add to Cart'; btn.disabled = false; }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step indicator & label
  // ═══════════════════════════════════════════════════════════════════════════

  _syncIndicator() {
    if (!this._indicator) return;
    const steps     = this.state.stepStates.map(s => s.category);
    const current   = this.state.currentStep;
    const isSummary = current >= steps.length;

    this._indicator.innerHTML = `<div class="mpb-breadcrumb">${
      steps.map((name, i) => {
        const isActive = !isSummary && i === current;
        const isDone   = i < current;
        const cls      = isActive ? ' active' : isDone ? ' done' : '';
        return `<div class="mpb-step-crumb${cls}">${name}</div>`;
      }).join('')
    }</div>`;
  }

  _syncLabel() {
    // Label hidden — the breadcrumb indicator above makes the step name self-evident.
    if (!this._stepLabel) return;
    this._stepLabel.textContent = '';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Debug
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Push third-party floating widgets (WhatsApp/Zoko, Tidio, Intercom…)
  // above the MPB sticky footer so they never overlap the Next/Back buttons.
  //
  // Strategy:
  //   1. Measure the sticky footer height and write --mpb-footer-h on :root
  //      so the CSS rule in meal-plan-builder.css can use it immediately.
  //   2. Apply bottom offset directly on any already-present widgets.
  //   3. Install a MutationObserver so widgets that inject themselves AFTER
  //      page load (Zoko, Tidio, etc.) are caught the moment they appear.
  _nudgeFloatingWidgets() {
    const SELECTORS = [
      '[id^="zoko-"]',
      '[id^="tidio-"]',
      '[id^="intercom-"]',
      '[class*="whatsapp-widget"]',
      '[class*="chat-widget"]',
      '[class*="zoko"]',
    ];

    const applyOffset = () => {
      const h      = this._footer?.offsetHeight || 55;
      const offset = h + 7;                            // 7px gap — sits just above footer

      // Update CSS variable so the stylesheet rule always has the live value
      document.documentElement.style.setProperty('--mpb-footer-h', `${h}px`);

      // Directly patch every matching widget element
      SELECTORS.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.style.setProperty('bottom', `${offset}px`, 'important');
        });
      });
    };

    // Run now (catches widgets already in DOM) and after full page load
    requestAnimationFrame(applyOffset);
    window.addEventListener('load', applyOffset, { once: true });
    window.addEventListener('resize', applyOffset, { passive: true });

    // MutationObserver — fires the instant Zoko/Tidio injects its element
    const observer = new MutationObserver(mutations => {
      const relevant = mutations.some(m =>
        [...m.addedNodes].some(n => {
          if (n.nodeType !== 1) return false;
          const id  = n.id  || '';
          const cls = n.className || '';
          return (
            id.startsWith('zoko-')   || id.startsWith('tidio-') ||
            id.startsWith('intercom-') ||
            (typeof cls === 'string' && (
              cls.includes('zoko') || cls.includes('whatsapp-widget') ||
              cls.includes('chat-widget')
            ))
          );
        })
      );
      if (relevant) applyOffset();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    // Store reference so destroy() can disconnect it
    this._widgetObserver = observer;
  }

  _logMetafields() {
    const mf = window.MPB_METAFIELDS;
    if (!mf || !Object.keys(mf).length) {
      console.warn('[MealPlanner] MPB_METAFIELDS empty — check Liquid handles match config.');
      return;
    }
    console.group('[MealPlanner] Metafields');
    Object.entries(mf).forEach(([handle, data]) => {
      console.groupCollapsed(handle);
      console.log('metafields:', data.metafields);
      // Highlight retention_info so it's easy to diagnose qty-chip population
      const ret = data.metafields?.retention_info;
      if (ret) {
        console.log('retention_info ✓', ret);
        const qm = (typeof ret === 'object') ? ret.quantity_multiplier : null;
        if (qm) console.log('  quantity_multiplier:', qm);
        else console.warn('  ⚠ quantity_multiplier missing inside retention_info');
      } else {
        console.warn('retention_info ✗ — null or not injected by Liquid for this handle');
      }
      console.log('variants:', data.variants);
      console.groupEnd();
    });
    console.groupEnd();
  }

  getSnapshot() {
    return {
      duration:     this.state.duration,
      currentStep:  this.state.currentStep,
      visitedSteps: [...this.state.visitedSteps],
      stepStates: this.state.stepStates.map(s => ({
        category:   s.category,
        activeMode: s.activeMode,
        selections: s.selections,
      })),
    };
  }

  // Public getter for selected items — use instead of window.selectedItem
  getSelectedItems() {
    return this.state.selectedItem;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Header-height detector
// Measures the tallest position:fixed / position:sticky element at the top of
// the page (the theme's nav) and writes it as --mpb-header-h on :root so the
// CSS padding-top of .mpb-page can clear it on every device.
// ═══════════════════════════════════════════════════════════════════════════════
(function applyHeaderOffset() {
  function measure() {
    let maxH = 0;

    // Check every fixed/sticky element whose bottom edge sits within the top
    // 20 % of the viewport — that's the header zone.
    document.querySelectorAll('*').forEach(el => {
      try {
        const st = window.getComputedStyle(el);
        const pos = st.position;
        if (pos !== 'fixed' && pos !== 'sticky') return;

        const rect = el.getBoundingClientRect();
        // Must be anchored to the top (top value ≤ 0 or very small)
        const topVal = parseFloat(st.top) || 0;
        if (topVal > 10) return;
        // Must sit in the top 20 % of the viewport
        if (rect.bottom > window.innerHeight * 0.20) return;

        if (rect.bottom > maxH) maxH = rect.bottom;
      } catch (_) {}
    });

    document.documentElement.style.setProperty('--mpb-header-h', `${Math.ceil(maxH)}px`);
  }

  // Run once DOM is ready, then again after fonts/images may have shifted layout
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', measure);
  } else {
    measure();
  }
  window.addEventListener('load', measure);              // after images load
  window.addEventListener('resize', measure, { passive: true }); // on rotate
}());

// ═══════════════════════════════════════════════════════════════════════════════
// Bootstrap — runs after all deferred scripts have executed
// ═══════════════════════════════════════════════════════════════════════════════
(function boot() {
  const cfg = window.MEAL_CONFIG;
  if (!cfg) {
    console.error('[MealPlanner] window.MEAL_CONFIG not defined. Load meal-plan-config.js first.');
    return;
  }
  const planner = new MealPlanner(cfg);
  planner.init();
  window.__mealPlanner = planner; // exposed for console debugging
}());
